import React, { useEffect, useState } from "react";
import { Activity, BarChart3, Bell, Mail, MapPin, Phone, Send } from "lucide-react";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Button } from "../components/ui/button";
import { contentService } from "../services/contentService";
import { APP_CONFIG } from "../constants/appConstants";
import { toast } from "sonner";

function Field({ label, ...props }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</label>
      <input required {...props} className="mt-2 w-full h-12 rounded-full px-5 bg-secondary/50 border border-border focus:outline-none focus:ring-2 focus:ring-accent" />
    </div>
  );
}

export default function Contact() {
  const [features, setFeatures] = useState([]);
  const [channels, setChannels] = useState([]);

  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${APP_CONFIG.ADMIN_API_BASE_URL}/content/contact-form`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || data?.message || "Failed to send");
      toast.success("Message saved");
      setForm({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Failed to send message", { description: err?.message || "Try again." });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const iconMap = { Activity, BarChart3, Bell, Mail, Phone, MapPin };
    let mounted = true;
    (async () => {
      try {
        const [f, c] = await Promise.all([contentService.listContactFeatures(), contentService.listContactChannels()]);
        if (!mounted) return;
        if (Array.isArray(f) && f.length) setFeatures(f.map((it) => ({ ...it, icon: iconMap[it.icon] || Activity })));
        if (Array.isArray(c) && c.length) setChannels(c.map((ch) => ({ ...ch, icon: iconMap[ch.icon] || Mail })));
      } catch (_e) {}
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <PageShell>
      <PageHero
        eyebrow="Contact"
        title="Write to us."
        highlight="Noida."
        description="Questions about a plot, a pump, or a board that will not come online."
      />

      <PageSection tone="secondary">
        <div className="mx-auto max-w-6xl">
          <h2 className="font-display text-3xl text-center mb-8">What the app covers</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl bg-card p-7 border border-border shadow-soft">
                <f.icon className="h-8 w-8 text-accent" />
                <h3 className="font-display text-2xl mt-4">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-2">{f.desc}</p>
              </div>
            ))}
          </div>
          {!features.length ? <div className="text-sm text-muted-foreground">No contact features available in database.</div> : null}
        </div>
      </PageSection>

      <PageSection>
        <div className="mx-auto max-w-6xl grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-accent mb-3">Reach us</div>
            <h2 className="font-display text-4xl">Send us a message</h2>
            <p className="mt-4 text-muted-foreground">Or use any of the channels below.</p>
            <div className="mt-8 space-y-5">
              {channels.map((c) => (
                <div key={c.label} className="flex gap-4 items-start">
                  <div className="h-11 w-11 rounded-2xl bg-accent/10 flex items-center justify-center text-accent shrink-0">
                    <c.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</div>
                    <div className="font-display text-xl mt-0.5">{c.value}</div>
                  </div>
                </div>
              ))}
            </div>
            {!channels.length ? <div className="text-sm text-muted-foreground mt-4">No contact channels available in database.</div> : null}
          </div>

          <form onSubmit={submit} className="lg:col-span-3 rounded-[2rem] bg-card border border-border p-8 md:p-10 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Your name"
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
              <Field
                label="Your phone"
                type="tel"
                placeholder="+91XXXXXXXXXX"
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Your email"
                type="email"
                placeholder="you@farm.com"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              />
              <Field
                label="Subject"
                placeholder="Demo request"
                value={form.subject}
                onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Your message</label>
              <textarea
                required
                rows={5}
                placeholder="Tell us about your farm…"
                className="mt-2 w-full rounded-2xl px-5 py-3 bg-secondary/50 border border-border focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                value={form.message}
                onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
              />
            </div>
            <Button type="submit" variant="accent" size="lg" className="w-full" disabled={submitting}>
              <Send className="h-4 w-4" /> Send message
            </Button>
          </form>
        </div>
      </PageSection>
    </PageShell>
  );
}
